import type { KpiTone } from '../types';
import type { ReactElement, ReactNode } from 'react';
import { Box, Paper, Stack, alpha, Skeleton, useTheme, Typography } from '@mui/material';

type KpiCardProps = {
  icon: ReactElement;
  title: ReactNode;
  value: ReactNode;
  helper: ReactNode;
  tone: KpiTone;
  loading: boolean;
};

const KpiCard = ({ icon, title, value, helper, tone, loading }: KpiCardProps) => {
  const theme = useTheme();
  const color = theme.palette[tone].main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        border: '1px solid',
        borderColor: alpha(color, theme.palette.mode === 'dark' ? 0.34 : 0.2),
        bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.11 : 0.045),
      }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            display: 'grid',
            flexShrink: 0,
            placeItems: 'center',
            color,
            borderRadius: 2,
            bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.2 : 0.11),
            '& .MuiSvgIcon-root': { fontSize: 23 },
          }}>
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={90} height={40} />
          ) : (
            <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 800, lineHeight: 1.15 }}>
              {value}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {helper}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
};

export default KpiCard;
